/* =====================================================================================
   Commercial types and configuration, with no data attached.

   Split out for the same reason atlas.core.ts was: Vite bundles a MODULE, not a SYMBOL. Pipeline
   imported STAGES, which is configuration, and that one import pulled the entire commercial file
   into every build, carrying Scira's opportunity pipeline with named hospitals and a real
   counterparty into bundles that had no business holding it.

   The demonstration builds looked clean because the bundler could fold the unused arrays away
   once it had proved the tenant gate false. The showcase build resolves its tenant at runtime,
   so the bundler can prove nothing, and the leak became visible. It was always there.

   NOTHING IN THIS FILE MAY IMPORT DATA. That is the whole point of it.
   ===================================================================================== */

export type Stage = 'identified' | 'qualified' | 'evaluation' | 'business_case' | 'procurement' | 'won' | 'lost';

export interface Opportunity {
  id: string;
  ref: string;
  name: string;
  /** The four parties. In transplant these are usually different organisations. */
  demandAccount: string;      // the transplant centre that creates clinical demand
  buyingAccount: string;      // usually the H&I lab, which holds the budget
  payerAccount?: string;      // the commissioner who decides funding
  procurementAccount?: string; // the body that runs the process
  siteId?: string;
  country: string;
  stage: Stage;
  saleType: 'direct' | 'distributor' | 'tender' | 'service_lab' | 'trial';
  partner?: string;
  amount: number;
  currency: string;
  recurringAnnual?: number;
  expectedClose: string;
  estimatedTestsYr?: number;
  pricePerTest?: number;
  champion?: string;
  economicBuyer?: string;
  competitor?: string;
  incumbentContractEnd?: string;
  blockedOnReimbursement: boolean;
  reimbursementRoute?: string;
  nextStep: string;
  nextStepDue: string;
  owner: string;
  daysInStage: number;
  slipped?: boolean;
  note?: string;
}

/* Neutral names. A live tenant's board reads its own stage names from pipeline.stages (Scira's
   say "Clinical evaluation" and "Procurement"); these are what a tenant without a database
   sees, and "Clinical evaluation" over a pharmaceutical demonstration was one industry's word. */
export const STAGES: { key: Stage; label: string; probability: number }[] = [
  { key: 'identified', label: 'Identified', probability: 5 },
  { key: 'qualified', label: 'Qualified', probability: 20 },
  { key: 'evaluation', label: 'Evaluation', probability: 40 },
  { key: 'business_case', label: 'Business case', probability: 60 },
  { key: 'procurement', label: 'Negotiation', probability: 80 },
  { key: 'won', label: 'Won', probability: 100 },
  { key: 'lost', label: 'Lost', probability: 0 },
];


export interface Partner {
  id: string;
  name: string;
  country: string;
  type: 'distributor' | 'agent' | 'authorised_representative' | 'service_lab';
  status: 'prospective' | 'due_diligence' | 'contracted' | 'active' | 'suspended' | 'terminated';
  territories: { country: string; product: string; exclusivity: 'exclusive' | 'semi_exclusive' | 'non_exclusive'; until: string }[];
  commitmentValue?: number;
  commitmentCurrency?: string;
  achievedPct?: number;
  dueDiligence: {
    status: 'none' | 'in_progress' | 'approved' | 'expired' | 'rejected';
    riskRating?: 'low' | 'medium' | 'high' | 'unacceptable';
    nextReview?: string;
    sanctionsScreened: boolean;
    pepScreened: boolean;
    abacSigned: boolean;
  };
  regulatoryRole?: string;
  sellThroughDue?: string;
  sellThroughStatus?: 'due' | 'overdue' | 'submitted' | 'accepted';
  note?: string;
}

export interface Tender {
  id: string;
  ref: string;
  title: string;
  authority: string;
  country: string;
  estimatedValue: number;
  currency: string;
  published?: string;
  clarificationDeadline?: string;
  submissionDeadline: string;
  contractStart?: string;
  contractMonths?: number;
  bidDecision: 'undecided' | 'bid' | 'no_bid' | 'withdrawn';
  status: 'identified' | 'qualifying' | 'preparing' | 'submitted' | 'awarded_us' | 'awarded_other' | 'cancelled';
  partner?: string;
  note?: string;
}
